import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../../utils/content.util';
import { useNavigate, useParams } from 'react-router-dom';
import { DealerProspect } from '../../../models/dealer-prospect.model';
import { onError } from '../../../utils/error.util';
import { createDealerProspect, getDealerProspect, updateDealerProspect } from '../../../services/dealer-prospects.service';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Form, Formik } from 'formik';
import { DealerProspectSchema, dealerProspectInitialValues } from './validationSchema';
import { CreateDealerProspectRequest } from '../../../models/dealer-prospect.model';
import { isDev } from '../../../utils/utils';

export default function DealerProspectFormContainer() {
  const { isAuthenticated, currentUser } = useAppContext();
  const navigate = useNavigate();
  const { prospectId } = useParams();

  const [dealerProspect, setDealerProspect] = useState<DealerProspect>(dealerProspectInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (prospectId && prospectId !== 'new') {
      loadDealerProspect();
    } else {
      // Set initial values for new lead
      const fullName = `${currentUser?.given_name ?? ''} ${currentUser?.family_name ?? ''}`.trim();
      setDealerProspect({
        ...dealerProspectInitialValues,
        teamMemberName: fullName,
        // division: currentUser?.['custom:division'] === 'CCL' ? Division.CCL : Division.TSF,
      });
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, prospectId, currentUser]);

  const loadDealerProspect = async () => {
    try {
      const response = await getDealerProspect(prospectId!);
      setDealerProspect(response);
    } catch (err) {
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: CreateDealerProspectRequest) => {
    setIsSubmitting(true);
    try {
      if (prospectId && prospectId !== 'new') {
        await updateDealerProspect(prospectId, values);
      } else {
        await createDealerProspect(values);
      }
      navigate('/dealer-prospects');
    } catch (err) {
      onError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <CircularProgressComponent />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <h2>{prospectId === 'new' ? 'Add New Dealer Prospect' : 'Edit Dealer Prospect'}</h2>
      </Box>

      <Formik enableReinitialize initialValues={dealerProspect} validationSchema={DealerProspectSchema} onSubmit={onSubmit}>
        {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
          <Form noValidate>
            <Grid container spacing={3}>
              {/* Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Date"
                  name="date"
                  type="date"
                  value={values.date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.date && Boolean(errors.date)}
                  helperText={touched.date && errors.date}
                  disabled
                />
              </Grid>

              {/* Division */}
              {/* <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth error={touched.division && Boolean(errors.division)}>
                  <InputLabel>Division</InputLabel>
                  <Select
                    name="division"
                    value={values.division}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Division"
                  >
                    <MenuItem value={Division.CCL}>CCL</MenuItem>
                    <MenuItem value={Division.TSF}>TSF</MenuItem>
                  </Select>
                  {touched.division && errors.division && <FormHelperText>{errors.division}</FormHelperText>}
                </FormControl>
              </Grid> */}

              {/* Created by */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Created by"
                  name="teamMemberName"
                  value={values.teamMemberName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.teamMemberName && Boolean(errors.teamMemberName)}
                  helperText={touched.teamMemberName && errors.teamMemberName}
                  disabled
                />
              </Grid>

              {/* Prospect Name (pushes to new field in Salesforce) */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Prospect Name"
                  name="leadTypeName"
                  value={values.leadTypeName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.leadTypeName && Boolean(errors.leadTypeName)}
                  helperText={touched.leadTypeName && errors.leadTypeName}
                />
              </Grid>

              {/* Dealership Name */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Dealership Name"
                  name="dealershipName"
                  value={values.dealershipName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.dealershipName && Boolean(errors.dealershipName)}
                  helperText={touched.dealershipName && errors.dealershipName}
                />
              </Grid>

              {/* Contact First Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Contact First Name"
                  name="contactFirstName"
                  value={values.contactFirstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactFirstName && Boolean(errors.contactFirstName)}
                  helperText={touched.contactFirstName && errors.contactFirstName}
                />
              </Grid>

              {/* Contact Last Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Contact Last Name"
                  name="contactLastName"
                  value={values.contactLastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactLastName && Boolean(errors.contactLastName)}
                  helperText={touched.contactLastName && errors.contactLastName}
                />
              </Grid>

              {/* Phone */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                  placeholder="(xxx) xxx-xxxx"
                />
              </Grid>

              {/* Email */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>

              {/* Address Line 1 */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  name="physicalAddress.addressLine1"
                  value={values.physicalAddress.addressLine1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.physicalAddress?.addressLine1 && Boolean(errors.physicalAddress?.addressLine1)}
                  helperText={touched.physicalAddress?.addressLine1 && errors.physicalAddress?.addressLine1}
                />
              </Grid>

              {/* Address Line 2 */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  name="physicalAddress.addressLine2"
                  value={values.physicalAddress.addressLine2 || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.physicalAddress?.addressLine2 && Boolean(errors.physicalAddress?.addressLine2)}
                  helperText={touched.physicalAddress?.addressLine2 && errors.physicalAddress?.addressLine2}
                />
              </Grid>

              {/* City */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="City"
                  name="physicalAddress.city"
                  value={values.physicalAddress.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.physicalAddress?.city && Boolean(errors.physicalAddress?.city)}
                  helperText={touched.physicalAddress?.city && errors.physicalAddress?.city}
                />
              </Grid>

              {/* State */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="State"
                  name="physicalAddress.state"
                  value={values.physicalAddress.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.physicalAddress?.state && Boolean(errors.physicalAddress?.state)}
                  helperText={touched.physicalAddress?.state && errors.physicalAddress?.state}
                />
              </Grid>

              {/* Zip Code */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  name="physicalAddress.zipCode"
                  value={values.physicalAddress.zipCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.physicalAddress?.zipCode && Boolean(errors.physicalAddress?.zipCode)}
                  helperText={touched.physicalAddress?.zipCode && errors.physicalAddress?.zipCode}
                />
              </Grid>

              {/* Submit Buttons */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : prospectId === 'new' ? 'Create Prospect' : 'Update Prospect'}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate('/dealer-prospects')} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Box>
              </Grid>

              {isDev ? (
                <>
                  <div>
                    <p>Initial Values:</p>
                    <code>{JSON.stringify(dealerProspect, null, 2)}</code>
                  </div>
                  <div>
                    <p>Errors:</p>
                    <code>{JSON.stringify(errors, null, 2)}</code>
                  </div>
                  <div>
                    <p>Current Values:</p>
                    <code>{JSON.stringify(values, null, 2)}</code>
                  </div>
                </>
              ) : null}
            </Grid>
          </Form>
        )}
      </Formik>
    </Container>
  );
}
